// Running a deck (docs/areas/learn.md): a Session is made when the deck starts and every card is
// saved as it is answered, as one undoable group, so closing mid-deck loses nothing and Undo always
// takes back exactly the last card. Every answer is graded by the exercise itself (its `answer` action),
// never marked by hand. Library code: it writes through the Mutator it is given.
import { mutate as runtimeMutate } from '@/runtime'
import type { Mutator } from '@/db/ops'
import { grade } from '@/kinds/learn/apply'
import type { ExerciseFormat, Familiarity, Result } from '@/kinds/learn/schema'
import type { Deck, DeckCard, ExerciseCard } from './learnDeck'

/**
 * What a person did with a card. Tiles are indexes into the exercise's choices (more than one on a
 * multi-select); `timeout` is the quick pick's soft timer running out, which grades nothing.
 */
export type Answer =
  | { kind: 'got' }
  | { kind: 'again' }
  | { kind: 'done' }
  | { kind: 'later' }
  | { kind: 'pick'; picked: number[] }
  | { kind: 'timeout' }

export interface Outcome {
  /** one line, said at once: what just happened */
  reason: string
  /** how the card flashes */
  tone: 'got' | 'again'
  /** how a question went; unset for a card that is not a question, and for a timeout */
  result?: Result
  /** the tiles that were right, so the card can show them */
  correct?: number[]
  timedOut?: boolean
  /** a concept got, or a lesson finished: what the summary says went right */
  win?: { kind: 'concept' | 'lesson'; name: string }
}

/** Starts the session for a deck and returns its id. */
export const startSession = async (
  deck: Deck,
  minutes: number,
  now: Date = new Date(),
  mutate: Mutator = runtimeMutate,
): Promise<string> =>
  (
    await mutate.create('Session', {
      started_at: now.toISOString(),
      planned_minutes: Math.max(1, Math.round(minutes)),
      mode: deck.mode,
    })
  ).id

/**
 * The familiarity a plain concept card (one with no exercises) moves a concept to: "got it" climbs one
 * step, "show again soon" drops a known one to learning and brings a new one in. undefined is no change.
 * Concepts with exercises are moved by the review scheduler on the server instead.
 */
export const familiarityAfter = (now: Familiarity, answer: 'got' | 'again'): Familiarity | undefined => {
  if (answer === 'got') return now === 'new' ? 'learning' : now === 'learning' ? 'known' : undefined
  return now === 'learning' ? undefined : 'learning'
}

const CONCEPT_REASON: Record<string, string> = {
  'got:new': 'Got it. Marked as learning.',
  'got:learning': 'Got it. Known now.',
  'got:known': 'Got it. Already known.',
  'again:new': 'No problem. Marked as learning.',
  'again:learning': 'No problem. It will come round again.',
  'again:known': 'No problem. Back to learning.',
}

const LEAD: Record<Result, string> = { right: 'Right.', partial: 'Partly.', wrong: 'Not quite.' }

const names = (card: ExerciseCard) => card.concepts.map((c) => c.spec.name).join(' and ')

/** The labels of the tiles that are right, for a line saying what the answer was. */
const answerLine = (card: ExerciseCard) =>
  (card.exercise.spec.choices ?? [])
    .filter((c) => c.correct)
    .map((c) => c.label)
    .join(' and ')

const gradeOutcome = (card: ExerciseCard, picked: number[]): Outcome => {
  const choices = card.exercise.spec.choices ?? []
  const result = grade(choices, picked)
  const why = card.exercise.spec.explanation ?? ''
  return {
    result,
    tone: result === 'right' ? 'got' : 'again',
    correct: choices.flatMap((c, i) => (c.correct ? [i] : [])),
    reason: `${LEAD[result]} ${why}`.trim(),
    win: result === 'right' ? { kind: 'concept', name: names(card) } : undefined,
  }
}

/** What a question says when its timer ran out: the answer, and that it will be back. Nothing is graded. */
const timeoutOutcome = (card: ExerciseCard): Outcome => ({
  tone: 'again',
  timedOut: true,
  correct: (card.exercise.spec.choices ?? []).flatMap((c, i) => (c.correct ? [i] : [])),
  reason: `Time is up. It was ${answerLine(card)}. This one comes back later.`,
})

/**
 * Saves one answer: the session covers the card's thing, the card grades and records itself, and the
 * session counts it and how it went, as one group. The last card also ends the session, so undoing it
 * reopens the card, not just the end. A timeout only covers the card (it is not offered again for half
 * an hour, then it is back): no grade, so nothing for the scheduler to read.
 */
export const answerCard = async (
  sessionId: string,
  card: DeckCard,
  answer: Answer,
  last: boolean,
  mutate: Mutator = runtimeMutate,
): Promise<Outcome> =>
  mutate.grouped(async () => {
    await mutate.setEdge(sessionId, card.id, 'covers')
    let out: Outcome
    if (card.format === 'concept') {
      const was = card.concept.status.familiarity
      const a = answer.kind === 'got' ? 'got' : 'again'
      const to = familiarityAfter(was, a)
      if (to) await mutate.action(card.id, 'set_familiarity', { state: to })
      await mutate.action(sessionId, 'item_done', { result: a === 'got' ? 'right' : 'partial' })
      out = {
        reason: CONCEPT_REASON[`${a}:${was}`]!,
        tone: a,
        win: a === 'got' ? { kind: 'concept', name: card.concept.spec.name } : undefined,
      }
    } else if (card.format === 'lesson') {
      if (answer.kind === 'done') {
        if (card.lesson.status.state !== 'done') await mutate.action(card.id, 'mark_done')
        await mutate.action(sessionId, 'item_done')
        out = { reason: 'Lesson done.', tone: 'got', win: { kind: 'lesson', name: card.lesson.spec.title } }
      } else {
        out = { reason: 'Not now. It will be here next time.', tone: 'again' }
      }
    } else if (card.format === 'learn') {
      const got = answer.kind === 'got'
      await mutate.action(card.id, 'answer', { got })
      await mutate.action(sessionId, 'item_done', { result: got ? 'right' : 'partial' })
      out = {
        tone: got ? 'got' : 'again',
        reason: got ? 'Got it.' : 'No problem. It will come round again.',
        win: got ? { kind: 'concept', name: names(card) } : undefined,
      }
    } else if (answer.kind === 'pick') {
      out = gradeOutcome(card, answer.picked)
      await mutate.action(card.id, 'answer', { picked: answer.picked })
      await mutate.action(sessionId, 'item_done', { result: out.result })
    } else {
      out = timeoutOutcome(card)
    }
    if (last) await mutate.action(sessionId, 'end')
    return out
  })

/** The formats that grade themselves from tiles. */
export const isQuestion = (f: DeckCard['format']): f is ExerciseFormat & ('judge' | 'quick_pick' | 'spot') =>
  f === 'judge' || f === 'quick_pick' || f === 'spot'
